/**
 * Schema.org JSON-LD Builder für Starter-Projekte (shared + Prerender).
 */
const trim = (v) => (typeof v === 'string' ? v.trim() : '');
const formatPrice = (priceCent) => {
    if (priceCent === undefined || priceCent === null || Number.isNaN(priceCent))
        return undefined;
    return (priceCent / 100).toFixed(2);
};
const buildPostalAddress = (input) => {
    const streetLine = [trim(input.street), trim(input.houseNumber)].filter(Boolean).join(' ');
    if (!streetLine && !trim(input.city))
        return undefined;
    return {
        '@type': 'PostalAddress',
        streetAddress: streetLine || undefined,
        postalCode: trim(input.zipCode) || undefined,
        addressLocality: trim(input.city) || undefined,
        addressCountry: trim(input.country) || 'DE'
    };
};
export const buildWebSiteSchema = (input) => {
    const base = input.baseUrl.replace(/\/+$/, '');
    const name = trim(input.projectName) || 'Restaurant';
    const alt = trim(input.shortName);
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${base}/#website`,
        name,
        ...(alt && alt !== name ? { alternateName: [alt] } : {}),
        url: `${base}/`,
        publisher: { '@id': `${base}/#restaurant` }
    };
};
export const buildRestaurantSchema = (input) => {
    const base = input.baseUrl.replace(/\/+$/, '');
    const name = trim(input.projectName) || 'Restaurant';
    const schema = {
        '@context': 'https://schema.org',
        '@type': ['Restaurant', 'FoodEstablishment', 'LocalBusiness'],
        '@id': `${base}/#restaurant`,
        name,
        url: `${base}/`,
        ...(trim(input.description) ? { description: trim(input.description) } : {}),
        ...(trim(input.phone) ? { telephone: trim(input.phone) } : {}),
        ...(trim(input.email) ? { email: trim(input.email) } : {}),
        image: trim(input.ogImageUrl) || `${base}/og-image.jpg`,
        ...(trim(input.logoUrl) ? { logo: trim(input.logoUrl) } : {}),
        address: buildPostalAddress(input),
        servesCuisine: (input.servesCuisine || []).filter(Boolean).join(', ') || undefined,
        priceRange: input.priceRange || '€€',
        hasMenu: `${base}/speisekarte`,
        potentialAction: {
            '@type': 'OrderAction',
            target: `${base}/`
        }
    };
    if (input.lat !== undefined && input.lng !== undefined) {
        schema.geo = {
            '@type': 'GeoCoordinates',
            latitude: input.lat,
            longitude: input.lng
        };
    }
    if (input.averageRating && input.reviewCount && input.reviewCount > 0) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: input.averageRating,
            reviewCount: input.reviewCount
        };
    }
    return schema;
};
export const buildMenuSchema = (input, categories) => {
    if (!categories.length)
        return null;
    const base = input.baseUrl.replace(/\/+$/, '');
    const sections = categories
        .filter((cat) => cat.name && Array.isArray(cat.products) && cat.products.length > 0)
        .map((cat) => ({
        '@type': 'MenuSection',
        name: cat.name,
        hasMenuItem: (cat.products || []).slice(0, 50).map((product) => {
            const price = formatPrice(product.priceCent);
            return {
                '@type': 'MenuItem',
                name: product.name,
                ...(trim(product.description) ? { description: trim(product.description) } : {}),
                ...(product.image ? { image: product.image } : {}),
                ...(price
                    ? {
                        offers: {
                            '@type': 'Offer',
                            price,
                            priceCurrency: 'EUR',
                            availability: 'https://schema.org/InStock'
                        }
                    }
                    : {})
            };
        })
    }));
    if (!sections.length)
        return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'Menu',
        '@id': `${base}/speisekarte#menu`,
        name: 'Speisekarte',
        hasMenuSection: sections
    };
};
export const buildStructuredDataGraph = (input, categories = [], options = {}) => {
    const graph = [
        buildWebSiteSchema(input),
        buildRestaurantSchema(input)
    ];
    if (options.includeMenu !== false) {
        const menu = buildMenuSchema(input, categories);
        if (menu)
            graph.push(menu);
    }
    return graph;
};
export const serializeStructuredDataScripts = (schemas) => schemas
    .map((schema, index) => `<script type="application/ld+json" id="structured-data-${index}">${JSON.stringify(schema)}</script>`)
    .join('\n');
export const injectStructuredDataIntoHtml = (html, schemas) => {
    if (!schemas.length)
        return html;
    const scripts = serializeStructuredDataScripts(schemas);
    if (html.includes('</head>')) {
        return html.replace('</head>', `${scripts}\n</head>`);
    }
    return `${scripts}\n${html}`;
};
export const injectStructuredData = injectStructuredDataIntoHtml;
